import { getSupabaseClient } from './supabaseClient';
import type {
  DecisionSignal,
  OutcomeObservation,
  JudgmentProfile,
  WorkspaceJudgmentInsights,
  ItemDecisionHistory,
  CaptureSignalInput,
  RecordOutcomeInput,
  JudgmentDomain,
  SignalJudgmentType,
} from '../types/decisionIntelligence';
import type { AnalyticsSignal } from './judgment/judgmentAnalytics';

/** Outcome types that count as a positive (successful) realized outcome. */
const POSITIVE_OUTCOMES = new Set(['shipped', 'succeeded', 'partial']);

// ─── Row mappers ─────────────────────────────────────────────────────────────

function rowToSignal(row: Record<string, unknown>): DecisionSignal {
  return {
    id:                    row.id as string,
    workspaceId:           row.workspace_id as string,
    itemId:                (row.item_id as string) || null,
    sessionId:             (row.session_id as string) || null,
    actorId:               row.actor_id as string,
    actorEmail:            (row.actor_email as string) || undefined,
    confidenceScore:       row.confidence_score as number,
    recommendation:        row.recommendation as DecisionSignal['recommendation'],
    rationale:             (row.rationale as string) || null,
    expectedOutcome:       (row.expected_outcome as string) || null,
    expectedTimelineDays:  (row.expected_timeline_days as number) || null,
    riskFlags:             (row.risk_flags as string[]) || [],
    contextSnapshot:       (row.context_snapshot as Record<string, unknown>) || {},
    domain:                (row.domain as JudgmentDomain) || 'general',
    judgmentType:          (row.judgment_type as DecisionSignal['judgmentType']) || 'outcome_prediction',
    stakes:                (row.stakes as number) ?? null,
    createdAt:             row.created_at as string,
    attribution: attributionOf(row),
  };
}

/**
 * The score on a signal, once an outcome has been logged on its item: the row's
 * own `attribution`, or the latest of the `decision_attributions` joined to it
 * (getRecentSignals). Null until then.
 */
function attributionOf(row: Record<string, unknown>): DecisionSignal['attribution'] {
  const joined = row.decision_attributions as Record<string, unknown>[] | undefined;
  const latest = joined?.length
    ? [...joined].sort((a, b) => String(b.computed_at).localeCompare(String(a.computed_at)))[0]
    : null;
  const attribution = (row.attribution as Record<string, unknown> | null | undefined) ?? latest;
  if (!attribution) return null;
  return {
    outcomeAligned:   attribution.outcome_aligned as boolean,
    calibrationError: attribution.calibration_error as number,
  };
}

function rowToObservation(row: Record<string, unknown>): OutcomeObservation {
  return {
    id:                   row.id as string,
    workspaceId:          row.workspace_id as string,
    itemId:               (row.item_id as string) || null,
    observedAt:           row.observed_at as string,
    outcomeType:          row.outcome_type as OutcomeObservation['outcomeType'],
    outcomeQualityScore:  (row.outcome_quality_score as number) || null,
    outcomeMetrics:       (row.outcome_metrics as Record<string, unknown>) || {},
    executionSource:      (row.execution_source as string) || null,
    notes:                (row.notes as string) || null,
    createdBy:            row.created_by as string,
    createdAt:            row.created_at as string,
  };
}

function rowToProfile(row: Record<string, unknown>): JudgmentProfile {
  return {
    userId:                    row.user_id as string,
    email:                     (row.email as string) || undefined,
    workspaceId:               row.workspace_id as string,
    domain:                    (row.domain as JudgmentDomain) || 'general',
    calibrationScore:          row.calibration_score != null ? Number(row.calibration_score) : null,
    confidenceAppropriateness: row.confidence_appropriateness != null ? Number(row.confidence_appropriateness) : null,
    proceedAccuracy:           row.proceed_accuracy != null ? Number(row.proceed_accuracy) : null,
    signalCount:               (row.signal_count as number) || 0,
    resolvedCount:             (row.resolved_count as number) || 0,
    computedAt:                row.computed_at as string,
  };
}

// ─── Signal capture ───────────────────────────────────────────────────────────

/**
 * Capture a decision signal, with the Judgment Analytics axes (judgment_type +
 * stakes) — the capture_judgment_signal RPC (migration 606). It is the strict
 * superset of the retired capture_decision_signal (EXECUTE revoked, mig 765).
 * belief and confidence are carried by recommendation + confidenceScore.
 */
export async function captureJudgmentSignal(
  workspaceId: string,
  input: CaptureSignalInput,
  itemId?: string,
  sessionId?: string
): Promise<{ signalId: string }> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('capture_judgment_signal', {
    p_workspace_id:            workspaceId,
    p_confidence_score:        input.confidenceScore,
    p_recommendation:          input.recommendation,
    p_judgment_type:           input.judgmentType ?? 'outcome_prediction',
    p_stakes:                  input.stakes ?? null,
    p_item_id:                 itemId ?? null,
    p_session_id:              sessionId ?? null,
    p_rationale:               input.rationale ?? null,
    p_expected_outcome:        input.expectedOutcome ?? null,
    p_expected_timeline_days:  input.expectedTimelineDays ?? null,
    p_risk_flags:              input.riskFlags ?? [],
    p_context_snapshot:        {},
    p_domain:                  input.domain ?? 'general',
  });

  if (error) throw new Error(`Failed to capture judgment signal: ${error.message}`);
  if (!data?.success) throw new Error(data?.error ?? 'Unknown error capturing judgment signal.');

  return { signalId: data.signal_id as string };
}

// ─── Outcome observation ──────────────────────────────────────────────────────

export async function recordOutcomeObservation(
  workspaceId: string,
  input: RecordOutcomeInput,
  itemId?: string
): Promise<{ observationId: string; attributionsComputed: boolean }> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('record_outcome_observation', {
    p_workspace_id:           workspaceId,
    p_outcome_type:           input.outcomeType,
    p_item_id:                itemId ?? null,
    p_outcome_quality_score:  input.outcomeQualityScore ?? null,
    p_outcome_metrics:        input.outcomeMetrics ?? {},
    p_execution_source:       input.executionSource ?? 'manual',
    p_notes:                  input.notes ?? null,
  });

  if (error) throw new Error(`Failed to record outcome: ${error.message}`);
  if (!data?.success) throw new Error(data?.error ?? 'Unknown error recording outcome.');

  return {
    observationId:       data.observation_id as string,
    attributionsComputed: data.attributions_computed as boolean,
  };
}

// ─── Workspace insights ───────────────────────────────────────────────────────

export async function getWorkspaceJudgmentInsights(
  workspaceId: string,
  domain?: JudgmentDomain
): Promise<WorkspaceJudgmentInsights> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('get_workspace_judgment_insights', {
    p_workspace_id: workspaceId,
    p_domain:       domain ?? null,
  });

  if (error) throw new Error(`Failed to load judgment insights: ${error.message}`);
  if (!data?.success) throw new Error(data?.error ?? 'Unknown error loading insights.');

  return {
    profiles:           ((data.profiles as Record<string, unknown>[]) || []).map(rowToProfile),
    totalSignals:       (data.total_signals as number) || 0,
    totalObservations:  (data.total_observations as number) || 0,
  };
}

// ─── Item decision history ────────────────────────────────────────────────────

export async function getItemDecisionHistory(
  itemId: string,
  workspaceId: string
): Promise<ItemDecisionHistory> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('get_item_decision_history', {
    p_item_id:      itemId,
    p_workspace_id: workspaceId,
  });

  if (error) throw new Error(`Failed to load decision history: ${error.message}`);
  if (!data?.success) throw new Error(data?.error ?? 'Unknown error loading history.');

  return {
    signals:      ((data.signals as Record<string, unknown>[]) || []).map(rowToSignal),
    observations: ((data.observations as Record<string, unknown>[]) || []).map(rowToObservation),
  };
}

// ─── Direct table reads ───────────────────────────────────────────────────────

export async function getRecentSignals(
  workspaceId: string,
  limit = 20
): Promise<DecisionSignal[]> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('decision_signals')
    .select('*, decision_attributions(outcome_aligned, calibration_error, computed_at)')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw new Error(`Failed to load signals: ${error.message}`);
  return (data || []).map(row => rowToSignal(row as Record<string, unknown>));
}

export async function getRecentObservations(
  workspaceId: string,
  limit = 20
): Promise<OutcomeObservation[]> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('outcome_observations')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('observed_at', { ascending: false })
    .limit(limit);

  if (error) throw new Error(`Failed to load observations: ${error.message}`);
  return (data || []).map(row => rowToObservation(row as Record<string, unknown>));
}

// ─── My own judgment profile ──────────────────────────────────────────────────

// ─── Judgment Analytics read model ────────────────────────────────────────────

/**
 * Load the workspace's signals joined to their resolved outcomes and persisted
 * consensus reference, shaped as `AnalyticsSignal[]` for the pure read model
 * (buildCellProfiles / buildTrustMap). Emails are resolved by the UI from its
 * member store, so they are left undefined here.
 */
export async function loadJudgmentAnalyticsSignals(
  workspaceId: string
): Promise<AnalyticsSignal[]> {
  const supabase = getSupabaseClient();

  const { data: signals, error: sigErr } = await supabase
    .from('decision_signals')
    .select('id, actor_id, judgment_type, domain, recommendation, confidence_score, stakes, risk_flags')
    .eq('workspace_id', workspaceId);
  if (sigErr) throw new Error(`Failed to load signals: ${sigErr.message}`);

  const { data: attributions, error: attrErr } = await supabase
    .from('decision_attributions')
    .select('signal_id, consensus_probability, peer_count, observation:outcome_observations(outcome_type)');
  if (attrErr) throw new Error(`Failed to load attributions: ${attrErr.message}`);

  // signalId → resolved reference (latest attribution wins).
  const resolved = new Map<string, AnalyticsSignal['resolved']>();
  for (const a of (attributions ?? []) as Record<string, unknown>[]) {
    const obs = a.observation as { outcome_type?: string } | { outcome_type?: string }[] | null;
    const outcomeType = Array.isArray(obs) ? obs[0]?.outcome_type : obs?.outcome_type;
    if (!outcomeType) continue;
    resolved.set(a.signal_id as string, {
      outcome: POSITIVE_OUTCOMES.has(outcomeType) ? 1 : 0,
      consensusProbability: a.consensus_probability != null ? Number(a.consensus_probability) : 0.5,
      peerCount: (a.peer_count as number) ?? 0,
    });
  }

  return ((signals ?? []) as Record<string, unknown>[]).map((row): AnalyticsSignal => {
    const flags = row.risk_flags as unknown[] | null;
    return {
      actorId: row.actor_id as string,
      judgmentType: (row.judgment_type as SignalJudgmentType) || 'outcome_prediction',
      domain: (row.domain as JudgmentDomain) || 'general',
      recommendation: row.recommendation as AnalyticsSignal['recommendation'],
      confidence: (row.confidence_score as number) / 10,
      stakes: row.stakes != null ? (row.stakes as number) / 10 : null,
      riskFlagCount: Array.isArray(flags) ? flags.length : 0,
      resolved: resolved.get(row.id as string) ?? null,
    };
  });
}

export async function getMyJudgmentProfiles(
  workspaceId: string
): Promise<JudgmentProfile[]> {
  const supabase = getSupabaseClient();
  const me = (await supabase.auth.getUser()).data.user;

  const { data, error } = await supabase
    .from('judgment_profiles')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('user_id', me?.id ?? '')
    .order('resolved_count', { ascending: false });

  if (error) throw new Error(`Failed to load profiles: ${error.message}`);
  return (data || []).map(row => rowToProfile({ ...(row as Record<string, unknown>), email: me?.email }));
}
